import { Slide } from '@/shared/types';

export const TINY_VIEWPORT_WIDTH = 500;
export const SMALL_VIEWPORT_WIDTH = 1370;

export const THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED = 60; // 1 hour

export const MAX_CONCURRENT_SOCKETS = 1000;

export const ENRICHMENT_POLL_INTERVAL_MS = 1500;
export const ENRICHMENT_POLL_TIMEOUT_MS = 1000 * 60 * 5;

export const PPTX_POLL_INTERVAL_MS = 3000;
export const PPTX_POLL_MAX_ATTEMPTS = 200;

/** Matches timeline-service ``TITLE_MAX_LENGTH`` — enforced on create/update write paths. */
export const TIMELINE_TITLE_MAX_LENGTH = 255;

/** LaunchDarkly dashboard key and React SDK camelCase form (TR-3087). */
export const THREAT_REPORT_CREATORS_FLAG_KEY = 'threat-report-creators';
export const THREAT_REPORT_CREATORS_FLAG_CAMEL = 'threatReportCreators';

/** Chat history rows still being written — keep polling until status moves past these (see answer-item intermediate statuses). */
export function isEnrichmentHistoryPending(status: unknown): boolean {
  return status === 'initializing' || status === 'inprogress';
}

export const META_FILE_REGEX = /^((preservation[-_]\d+)|(records))\.html$/i;

/**
 * Matches the basename of a source file that should open in the standalone
 * read-only ``/email-viewer`` (Outlook-style mbox / pst / ost reader) rather
 * than the inline media preview. The viewer talks to the DB-backed
 * ``/v1/emails`` junkdrawer endpoints, so any format the loader can ingest
 * works here uniformly — no format-specific code lives in the SPA.
 */
export const EMAIL_FILE_REGEX = /\.(mbox|mbx|pst|ost)$/i;

/**
 * Storage key for ``/email-viewer?s3Key=`` when ``url`` is a mailbox container
 * (mbox / mbx / pst / ost). Null for every other file.
 */
export function emailViewerS3KeyFromUrl(url: string): string | null {
  if (!url) return null;
  const s3Key = url.startsWith('s3://')
    ? url.split('/').slice(3).join('/')
    : url;
  const basename = (s3Key.split(/[?#]/)[0].split('/').pop() || '').toLowerCase();
  return EMAIL_FILE_REGEX.test(basename) ? s3Key : null;
}

export const PROVENANCE_STATUS_GROUPS = {
  SUCCESS: ['completed', 'success'], // green with a check icon, tooltip: "Verified and trusted source"
  SKIPPED: ['skipped'], // medium gray with a slash icon, tooltip: "Skipped - unsupported file type"
  PARTIAL_SUCCESS: ['partial'], // same provenance UX as SUCCESS (see clientFunctions / provenance-icon)
  ERROR: ['failed'], // red with a warning icon, tooltip: "Source verification failed or rejected"
  INTERMEDIARY: ['delivery', 'processing'], // orange with a cloud arrow up icon, tooltip: "Source verification in progress"
};

export const FileTypes = {
  // '12345': 'image', // we check a numeric extension when guessing media type
  'jpg': 'image',
  'jpeg': 'image',
  'dng': 'image',
  'png': 'image',
  'gif': 'image',
  'bmp': 'image',
  'tiff': 'image',
  'tif': 'image',
  'webp': 'image',
  'svg': 'image',
  'pic': 'image',
  'pic_compressed': 'image',
  'thumb': 'image',
  'thumbnail': 'image',
  'thumnail': 'image',
  'jp2': 'image',
  'jxl': 'image',
  'heic': 'image',
  'heif': 'image',
  'wmf': 'image',
  'avif': 'image',
  'aac': 'audio',
  'mp3': 'audio',
  'm4a': 'audio',
  'wav': 'audio',
  'ogg': 'audio',
  'amr': 'audio',
  'slk': 'audio',
  'opus': 'audio',
  'wma': 'audio',
  'mp4': 'video',
  'm4v': 'video',
  'webm': 'video',
  'wmv': 'video',
  'mpg': 'video',
  'mpeg': 'video',
  'mov': 'video',
  'avi': 'video',
  'mkv': 'video',
  'flv': 'video',
  'dav': 'video',
  'pdf': 'pdf',
  'docx': 'docx',
  'doc': 'docx',
  'xls': 'spreadsheet',
  'xlsx': 'spreadsheet',
  'ppt': 'pptx',
  'pptx': 'pptx',
  'txt': 'text',
  'text': 'text',
  'xml': 'text',
  'md': 'markdown',
  'markdown': 'markdown',
  'msg': 'text',
  'eml': 'text',
  'mbox': 'text',
  'mbx': 'text',
  'pst': 'text',
  'ost': 'text',
  'emlx': 'text',
  'email': 'text',
  'pdfa': 'pdf',
  'vtt': 'text',
  'csv': 'spreadsheet',
  'tsv': 'spreadsheet',
  'html': 'html',
  'htm': 'html',
  'website': 'html',
  'url': 'html',
  'json': 'json',
}

// we now convert all videos and audio that can be converted, and the proxy route checks for the converted file
// export const convertedVideo = ['avi', 'mkv', 'flv'];

export const fileTypeControllers = {
  'PDF': ['pdf', 'pdfa'],
  'MS Office': ['pptx', 'docx', 'doc', 'xls', 'xlsx'],
  'Image': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'image').map((key) => key.toLowerCase()),
  'Video': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'video').map((key) => key.toLowerCase()),
  'Audio': Object.keys(FileTypes).filter((key) => (FileTypes as any)[key] === 'audio').map((key) => key.toLowerCase()),
  'Spreadsheet': ['xls', 'xlsx', 'csv', 'tsv'],
  'Email': ['msg', 'eml', 'mbox', 'mbx', 'pst', 'ost', 'emlx', 'msg', 'email', 'pdfa'],
  'Other': ['txt', 'html', 'website', 'url', 'json'],
}

export const UsableMediaTypes = ['image', 'video', 'audio', 'pdf', 'text', 'markdown', 'docx', 'pptx', 'html', 'spreadsheet'];

/**
 * Whether a media type can be rendered in MediaDisplay / source previews.
 * ``json`` is usable only when JSON media is enabled (legacy
 * ``jsonMediaDisplay`` or ``mediaPlayerTransition.json``). It is
 * intentionally omitted from ``UsableMediaTypes`` so ungated ``.includes``
 * checks never enable JSON display while those flags are off.
 */
export function isDisplayableMediaType(
  mediaType: string | undefined | null,
  options?: { jsonMediaDisplay?: boolean } | null,
): boolean {
  if (!mediaType) return false;
  if (mediaType === 'json') return !!options?.jsonMediaDisplay;
  return UsableMediaTypes.includes(mediaType);
}

// don't allow the download of these file types
export const RestrictedSourceTypes = [
  'ps1', 'ps2', 'psm1', 'psm2', 'psm3', 'psm4', 'psm5', 'psm6', 'psm7', 'psm8', 'psm9', 'psm10',
  'apk', 'app', 'deb', 'dmg', 'pkg', 'rpm', 'msi', 'appx', 'appxbundle', 'appxupload', 'msix', 'msixbundle', 'msixupload', 'appxbundle', 'appxupload', 'msixbundle', 'msixupload',
  'exe',
  'bat', 'cmd', 'sh',
  'com',
  'scr',
  'ps1',
  'dmg',
  'pkg',
  'iso',
  'img',
  'vhd',
  'vhdx',
  'apks',
  'dll',
  'xapk',
  'ipa',
  'jar',
  'class',
  'pyc',
  'pyo',
  'rb',
  'pl',
  'bin',
  'run',
  'elf',
  'so',
  'sys',
  'drv',
  'crx',
  'xpi',
  //'ufdr',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  'bz2',
  'json',
  'js',
  'ts',
  'css',
];

export const BLANK_SLIDE: Slide = {
  show: true,
  id: 'blank',
  displayOrder: 0,
  order: 0,
  date: '',
  time: '',
  slideTitle: '',
  slideSubTitle: '',
  slideDescription: '',
};

// LLM-related constants
export const REFUSAL_MESSAGES = [
  "Warning:",
  "Sorry, I am unable to assist you with this request",
  "I am unable to assist you",
  "I apologize, but",
  "I cannot assist",
  "I must decline",
  "I cannot provide",
  "I cannot answer",
  "I cannot assist with",
  "I cannot help with",
  "I cannot help you with",
  "I will not",
  "I cannot and should not"
];
export const PASS_MESSAGES = [
  "I apologize, but I do not see",
];
